import { useEffect } from 'react';
import { TrashIcon, CloseIcon } from '../../assets/icons';

interface DeleteOrganizationModalProps {
  /** The organization being deleted; null keeps the modal closed. */
  orgName?: string | null;
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/** Destructive confirmation modal for deleting an organization. */
export default function DeleteOrganizationModal({
  orgName,
  open,
  loading = false,
  onClose,
  onConfirm,
}: DeleteOrganizationModalProps) {
  // Close on Escape (ignored while the delete is in flight).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(11,28,48,0.2)] p-6 backdrop-blur-[2px]"
      onClick={loading ? undefined : onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-org-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[460px] overflow-hidden rounded-2xl bg-white shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1),0px_8px_10px_-6px_rgba(0,0,0,0.1)]"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-8 pt-8">
          <div className="flex items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500">
              <TrashIcon className="size-5" />
            </span>
            <h2
              id="delete-org-title"
              className="text-xl font-bold leading-7 tracking-[-0.4px] text-ink"
            >
              Delete Organization
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={loading}
            className="shrink-0 text-ink-secondary transition-colors hover:text-ink disabled:opacity-40"
          >
            <CloseIcon className="size-[14px]" />
          </button>
        </div>

        {/* Body */}
        <div className="px-8 pb-2 pt-4">
          <p className="text-sm leading-6 text-ink-secondary">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-ink">“{orgName ?? 'this organization'}”</span>? This
            action is permanent and cannot be undone.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-8 pb-8 pt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-[#727785] px-6 py-2.5 text-xs font-semibold tracking-[0.6px] text-ink transition-colors hover:bg-brand-shade disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg bg-red-500 px-7 py-2.5 text-xs font-semibold tracking-[0.6px] text-white shadow-btn transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
